import { cn } from '../../lib/cn';

export interface ProgressProps {
  /** 0–1. Values outside the range are clamped. */
  value: number;
  className?: string;
  tone?: 'primary' | 'gold' | 'violet' | 'brand';
  size?: 'sm' | 'md';
  label?: string;
}

const TONES = {
  primary: 'bg-primary',
  gold: 'bg-gold',
  violet: 'bg-violet',
  brand: 'bg-brand',
} as const;

export function Progress({ value, className, tone = 'primary', size = 'md', label }: ProgressProps) {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cn(
        'bg-surface-overlay w-full overflow-hidden rounded-full',
        size === 'sm' ? 'h-1' : 'h-2',
        className,
      )}
    >
      <div
        className={cn('ease-out-quint h-full rounded-full transition-[width] duration-[720ms]', TONES[tone])}
        style={{ width: `${clamped * 100}%` }}
      />
    </div>
  );
}

/** Circular variant used for the achievement completion dial. */
export function ProgressRing({
  value,
  size = 96,
  strokeWidth = 8,
  className,
  children,
}: {
  value: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  children?: React.ReactNode;
}) {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--line-strong)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--primary)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped)}
          style={{ transition: 'stroke-dashoffset 720ms var(--ease-out-quint)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
}
